import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface StatItem {
  value: number;
  suffix: string;
  prefix?: string;
  label: string;
  description: string;
  icon: string;
}

function useCountUp(target: number, duration: number, active: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);

  return count;
}

interface StatCardProps {
  stat: StatItem;
  delay: number;
  active: boolean;
}

function StatCard({ stat, delay, active }: StatCardProps) {
  const count = useCountUp(stat.value, 1800, active);

  return (
    <div
      className="group relative bg-white border border-[#E7E1D9] rounded-lg p-8 lg:p-10 hover:border-[#F97700] hover:shadow-xl transition-all duration-300 cursor-default overflow-hidden"
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Decorative background number */}
      <span className="absolute bottom-4 right-6 text-[72px] font-light text-[#FFF1E6] leading-none select-none pointer-events-none group-hover:text-[#FFF1E6]/80 transition-colors duration-300">
        {stat.prefix ?? ''}{stat.value}{stat.suffix}
      </span>

      {/* Icon */}
      <div className="w-10 h-10 flex items-center justify-center mb-6">
        <i className={`${stat.icon} text-3xl text-[#F97700]`}></i>
      </div>

      {/* Counter */}
      <div className="flex items-end gap-1 mb-2">
        {stat.prefix && (
          <span className="text-2xl font-semibold text-[#F97700] mb-1">{stat.prefix}</span>
        )}
        <span className="text-5xl font-semibold text-[#0B0B0B] leading-none tabular-nums">
          {count.toLocaleString('pt-BR')}
        </span>
        <span className="text-2xl font-semibold text-[#F97700] mb-1">{stat.suffix}</span>
      </div>

      {/* Label */}
      <p className="text-[13px] font-semibold text-[#0B0B0B] uppercase tracking-[0.06em] mb-2 relative z-10">
        {stat.label}
      </p>

      {/* Description */}
      <p className="text-[13px] text-[#5A5248] leading-relaxed relative z-10 max-w-[220px]">
        {stat.description}
      </p>
    </div>
  );
}

export default function StatsSection() {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const isPt = i18n.language === 'pt';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const stats: StatItem[] = isPt
    ? [
        {
          value: 180,
          suffix: 'M+',
          prefix: 'R$',
          label: 'Volume Estruturado',
          description: 'Em crédito patrimonial estruturado e acompanhado desde a fundação.',
          icon: 'ri-bar-chart-box-line',
        },
        {
          value: 240,
          suffix: '+',
          label: 'Clientes Atendidos',
          description: 'Famílias, empresários e investidores com diagnóstico e estruturação concluídos.',
          icon: 'ri-group-line',
        },
        {
          value: 5,
          suffix: '+',
          label: 'Anos de Experiência',
          description: 'Atuando em engenharia patrimonial com método, governança e rigor técnico.',
          icon: 'ri-time-line',
        },
        {
          value: 92,
          suffix: '%',
          label: 'Taxa de Satisfação',
          description: 'Dos clientes recomendam a FGT após a conclusão do processo de estruturação.',
          icon: 'ri-star-line',
        },
      ]
    : [
        {
          value: 180,
          suffix: 'M+',
          prefix: 'R$',
          label: 'Structured Volume',
          description: 'In structured and monitored wealth credit since foundation.',
          icon: 'ri-bar-chart-box-line',
        },
        {
          value: 240,
          suffix: '+',
          label: 'Clients Served',
          description: 'Families, entrepreneurs and investors with completed assessment and structuring.',
          icon: 'ri-group-line',
        },
        {
          value: 5,
          suffix: '+',
          label: 'Years of Experience',
          description: 'Operating in wealth engineering with method, governance and technical rigor.',
          icon: 'ri-time-line',
        },
        {
          value: 92,
          suffix: '%',
          label: 'Satisfaction Rate',
          description: 'Of clients recommend FGT after completing the structuring process.',
          icon: 'ri-star-line',
        },
      ];

  return (
    <section ref={sectionRef} className="bg-[#F6F2EC] py-24">
      <div className="max-w-[1440px] mx-auto px-8 lg:px-16">

        {/* Header */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-[11px] uppercase tracking-[0.12em] font-medium text-[#F97700] mb-3">
            {isPt ? 'NÚMEROS INSTITUCIONAIS' : 'INSTITUTIONAL NUMBERS'}
          </p>
          <h2 className="text-4xl font-semibold text-[#0B0B0B] mb-4">
            {isPt ? 'Resultados que falam por si' : 'Results that speak for themselves'}
          </h2>
          <p className="text-[15px] text-[#5A5248] max-w-[480px] mx-auto leading-relaxed">
            {isPt
              ? 'Dados consolidados da operação FGT desde o início das atividades de engenharia patrimonial.'
              : 'Consolidated FGT operation data since the beginning of wealth engineering activities.'}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {stats.map((stat, i) => (
            <div
              key={i}
              className={`transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${150 + i * 100}ms` }}
            >
              <StatCard stat={stat} delay={0} active={visible} />
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <p
          className={`text-center text-[12px] text-[#9A9288] mt-8 transition-all duration-700 delay-700 ${
            visible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <i className="ri-information-line mr-1"></i>
          {isPt
            ? 'Dados estimados com base no histórico operacional da FGT Capital. Não constituem projeção ou garantia de resultados futuros.'
            : 'Estimated data based on FGT Capital operational history. Does not constitute projection or guarantee of future results.'}
        </p>
      </div>
    </section>
  );
}
